import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowRight,
  FaExternalLinkAlt,
  FaWallet,
  FaMobileAlt,
} from "react-icons/fa";
import "./Header.css";

// Match these paths to your existing React routes.
const serviceGroups = [
  {
    title: "Technology & Digital Solutions",
    description: "Design, develop, and grow your digital business.",
    links: [
      ["Software Development", "/technology-digital-solutions"],
      ["Mobile App Development", "/technology-digital-solutions"],
      ["Website Development", "/technology-digital-solutions"],
      ["Website Design", "/technology-digital-solutions"],
      ["Graphics Design", "/technology-digital-solutions"],
      ["SEO Services", "/technology-digital-solutions"],
      ["UI/UX Design", "/technology-digital-solutions"],
      ["Technology Service Provider (TSP)", "/technology-digital-solutions"],
    ],
  },
  {
    title: "Payments & E-Governance",
    description: "Connected services for businesses and customers.",
    links: [
      ["E-Governance Services", "/payments-e-governance"],
      ["Bill Payment Service", "/payments-e-governance"],
      ["Banking Business Correspondent Service", "/payments-e-governance"],
      ["Payment Services", "/payments-e-governance"],
    ],
  },
];

const products = [
  {
    name: "Formax Pay",
    description: "Payment technology and business services for partners.",
    href: "https://formaxpay.com/",
    icon: FaWallet,
    className: "fit-header__product-icon--blue",
  },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const headerRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const menuButtonRefs = useRef({});
  const location = useLocation();

  const closeMenus = () => {
    setMobileOpen(false);
    setOpenMenu(null);
  };

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        setMobileOpen(false);
        setOpenMenu(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (openMenu) {
        menuButtonRefs.current[openMenu]?.focus();
        setOpenMenu(null);
      } else if (mobileOpen) {
        mobileButtonRef.current?.focus();
        setMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openMenu, mobileOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 1051px)");

    const handleBreakpoint = () => {
      setMobileOpen(false);
      setOpenMenu(null);
    };

    desktopQuery.addEventListener("change", handleBreakpoint);

    return () => {
      desktopQuery.removeEventListener("change", handleBreakpoint);
    };
  }, []);

  const menuButton = (name, label) => (
    <button
      type="button"
      ref={(element) => {
        menuButtonRefs.current[name] = element;
      }}
      className={`fit-header__menu-button ${
        openMenu === name ? "is-open" : ""
      }`}
      aria-expanded={openMenu === name}
      aria-controls={`fit-header-${name}`}
      onClick={() => {
        setOpenMenu((current) => (current === name ? null : name));
      }}
    >
      {label}
      <FaChevronDown aria-hidden="true" />
    </button>
  );

  return (
    <header
      className="fit-header"
      ref={headerRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          closeMenus();
        }
      }}
    >
      {/* Top contact bar */}
      <div className="fit-header__top">
        <div className="fit-header__container fit-header__top-inner">
          <p className="fit-header__top-message">
            Technology that connects. Solutions that grow.
          </p>

          <div className="fit-header__top-contact">
            <a href="tel:+919836114520">
              <FaPhoneAlt aria-hidden="true" />
              <span>+91 98361 14520</span>
            </a>

            <a href="mailto:info@formaxit.com">
              <FaEnvelope aria-hidden="true" />
              <span>info@formaxit.com</span>
            </a>

            <span className="fit-header__location">
              <FaMapMarkerAlt aria-hidden="true" />
              Barasat, Kolkata
            </span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="fit-header__container fit-header__main">
        <Link to="/" className="fit-header__logo" onClick={closeMenus}>
          <img
            src="/images/logo.png"
            alt="Formax IT Solutions — home"
            width="175"
            height="58"
          />
        </Link>

        <button
          type="button"
          ref={mobileButtonRef}
          className="fit-header__mobile-toggle"
          aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={mobileOpen}
          aria-controls="fit-header-navigation"
          onClick={() => {
            setMobileOpen((current) => !current);
            setOpenMenu(null);
          }}
        >
          {mobileOpen ? (
            <FaTimes aria-hidden="true" />
          ) : (
            <FaBars aria-hidden="true" />
          )}
        </button>

        <nav
          id="fit-header-navigation"
          className={`fit-header__nav ${mobileOpen ? "is-open" : ""}`}
          aria-label="Main navigation"
        >
          <ul className="fit-header__nav-list">
            <li>
              <NavLink
                to="/"
                end
                className="fit-header__nav-link"
                onClick={closeMenus}
              >
                Home
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/about-us"
                className="fit-header__nav-link"
                onClick={closeMenus}
              >
                About Us
              </NavLink>
            </li>

            {/* Products dropdown */}
            <li className="fit-header__dropdown fit-header__products">
              {menuButton("products", "Products")}

              <div
                id="fit-header-products"
                className="fit-header__panel fit-header__products-panel"
                hidden={openMenu !== "products"}
              >
                <div className="fit-header__panel-heading">
                  <span>OUR PRODUCTS</span>
                  <h2>Platforms built for everyday business</h2>
                </div>

                <div className="fit-header__product-list">
                  {products.map((product) => {
                    const Icon = product.icon;

                    return (
                      <a
                        key={product.name}
                        href={product.href}
                        className="fit-header__product-card"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${product.name} — opens in a new tab`}
                        onClick={closeMenus}
                      >
                        <span
                          className={`fit-header__product-icon ${product.className}`}
                        >
                          <Icon aria-hidden="true" />
                        </span>

                        <span className="fit-header__product-copy">
                          <strong>{product.name}</strong>
                          <span>{product.description}</span>
                        </span>

                        <FaExternalLinkAlt
                          className="fit-header__external-icon"
                          aria-hidden="true"
                        />
                      </a>
                    );
                  })}
                </div>
              </div>
            </li>

            {/* Categorised services */}
            <li className="fit-header__dropdown">
              {menuButton("services", "Services")}

              <div
                id="fit-header-services"
                className="fit-header__panel fit-header__services-panel"
                hidden={openMenu !== "services"}
              >
                <div className="fit-header__panel-heading">
                  <span>WHAT WE DO</span>
                  <h2>Solutions for your next stage of growth</h2>
                </div>

                <div className="fit-header__service-grid">
                  {serviceGroups.map((group) => (
                    <section
                      key={group.title}
                      className="fit-header__service-group"
                    >
                      <h3>{group.title}</h3>
                      <p>{group.description}</p>

                      <ul>
                        {group.links.map(([label, href]) => (
                          <li key={label}>
                            <Link to={href} onClick={closeMenus}>
                              <span>{label}</span>
                              <FaArrowRight aria-hidden="true" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            </li>

            <li>
              <NavLink
                to="/technology"
                className="fit-header__nav-link"
                onClick={closeMenus}
              >
                Technology
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/industries"
                className="fit-header__nav-link"
                onClick={closeMenus}
              >
                Industries
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/contact-us"
                className="fit-header__contact-button"
                onClick={closeMenus}
              >
                Contact Us
                <FaArrowRight aria-hidden="true" />
              </NavLink>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
